// template-v4.tsx — Outform's OWN estimate-template prototype, running on the
// board.
//
// WHAT THIS IS. On 28 August 2026 Outform sent two files: an interactive HTML
// prototype of their estimate output document (v4.1) and a Word change summary
// describing what moved from v2 → v4. The change summary is a KB page rendered
// with <DocumentFrame> beside this, because that is what the board law says a
// document is. THIS file is the other half: the prototype is not a document,
// it is an APPLICATION — nine tabs, an Internal/Client view toggle, a five-role
// governance switcher, an editable rate panel and a production-mix slider.
// Screenshotting it would throw away the only thing that makes it worth reading.
//
// WHY AN IFRAME, when nothing else on this board is one. Every other live
// surface here is our own React (the design versions, Univer, the file grid),
// so it composes. This is 204 KB of somebody else's HTML with its own CSS reset
// (`*{margin:0;padding:0}`), its own font stack and its own `#app` shell that
// claims `min-height:100vh`. Dropped inline it would reset the board's own
// styles and size itself against the browser window rather than its slot. An
// iframe is the only thing that gives a foreign document its own box.
//
// THE FILE IS ON R2, NOT IN THIS REPO, AND THAT IS DELIBERATE. `public/` is
// copied into the Pages deploy verbatim — which is exactly how one client's
// config reached another client's public domain on 20 Aug 2026. A client's own
// confidential estimate template ("NOT FOR ONWARD DISTRIBUTION" in its own
// footer) has no business being a tracked file in a repo that deploys to a
// public origin.
//
// ⚠️ The document is Outform's, not ours. It is headed "Prototype · Illustrative
// Data" by its own author, and its change summary marks five sections as
// fabricated placeholder data. Read the numbers accordingly — the analysis of
// what is real and what is not lives in the KB page next to it.

const SRC =
  "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/board/outform/template-v4/estimate-template-v4_1.html";

const FONT = "'Inter Tight', Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

export function OutformTemplateV4({
  label = "Their estimate template v4.1 — nine tabs, live. Internal / Client toggle, five roles",
  width = 1240,
  height = 1500,
}: {
  label?: string;
  width?: number;
  height?: number;
}) {
  return (
    <div style={{ width, fontFamily: FONT, display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Same caption strip as DocumentFrame, on purpose: this sits in a row
          beside three of them and a frame that captions itself differently
          reads as a different KIND of thing, which it is not. */}
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, paddingInline: 4 }}>
        <div style={{ fontFamily: MONO, fontSize: 12, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: "#8C948E" }}>
          {label}
        </div>
        <a
          href={SRC}
          target="_blank"
          rel="noreferrer"
          className="nodrag nopan"
          style={{ pointerEvents: "auto", fontFamily: MONO, fontSize: 11, color: "#8C948E", textDecoration: "none", whiteSpace: "nowrap" }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          open ↗
        </a>
      </div>

      <div
        // nowheel: scroll the prototype, do not zoom the canvas.
        // nodrag/nopan: a click inside it is a click on THEIR buttons, not the
        // start of a board pan. Both are the same two traps DocumentFrame
        // exists to get right; an iframe needs them for the identical reason.
        className="nowheel nodrag nopan"
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          pointerEvents: "auto",
          width,
          height,
          background: "#FFFFFF",
          border: "1px solid #E3DED4",
          borderRadius: 18,
          boxShadow: "0 10px 30px rgba(27,33,30,0.05)",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <iframe
          src={SRC}
          title="Outform Estimate Template v4.1"
          // No `allow-same-origin`: the document is somebody else's and it has
          // no reason to reach our storage or our cookies — and it does not
          // want to. Measured rather than assumed: the file contains no
          // localStorage, no sessionStorage, no cookie access, no indexedDB,
          // no fetch/XHR and no window.parent. It is pure DOM with inline
          // script, so `allow-scripts` is everything it needs to run.
          //
          // `allow-modals` is here for exactly one caller: their Export PDF
          // button, which is a bare `window.print()`. A sandbox blocks print
          // silently, so without this the one button on the page that does not
          // just re-render would appear broken — and "broken" would look like
          // our frame's fault rather than a sandbox flag.
          sandbox="allow-scripts allow-modals"
          // deliberately NOT data-zoom: double-click belongs to THEIR page here,
          // because their page has things to click. Use the section label to
          // fly the camera instead.
          style={{ width: "100%", height: "100%", border: 0, display: "block" }}
        />
      </div>
    </div>
  );
}
